import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Search, MapPin, TrendingUp, Filter } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StoryCard from "@/components/StoryCard";
import useStoryCard from "@/components/share/useStoryCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const RankingsPage = () => {
  const [rankings, setRankings] = useState([]);
  const [filteredRankings, setFilteredRankings] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("score");
  const [loading, setLoading] = useState(true);

  // For hidden story cards (one per listing)
  const storyRefs = useRef({});

  useEffect(() => {
    fetchRankings();
  }, []);

  useEffect(() => {
    filterAndSortRankings();
  }, [rankings, searchQuery, sortBy]);

  const fetchRankings = async () => {
    try {
      const response = await axios.get(`${API}/rankings/global`);
      setRankings(response.data);
    } catch (error) {
      console.error("Error fetching rankings:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortRankings = () => {
    let filtered = [...rankings];

    if (searchQuery) {
      filtered = filtered.filter(
        (item) =>
          item.destination.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.destination.country.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (sortBy === "score") {
      filtered.sort((a, b) => b.metrics.composite_wsi - a.metrics.composite_wsi);
    } else if (sortBy === "name") {
      filtered.sort((a, b) => a.destination.city.localeCompare(b.destination.city));
    } else if (sortBy === "trend") {
      filtered.sort((a, b) => b.metrics.trend_7day - a.metrics.trend_7day);
    }

    setFilteredRankings(filtered);
  };

  // Text-only sharing
  const shareWhatsAppText = (title, url) => {
    const msg = encodeURIComponent(`Check the Women Safety Score for ${title}: ${url}`);
    window.open(`https://api.whatsapp.com/send?text=${msg}`, "_blank");
  };

  // Native share sheet
  const shareNative = async (title, url) => {
    if (navigator.share) {
      await navigator.share({
        title,
        text: `Women Safety Score for ${title}`,
        url,
      });
    } else {
      alert("Device does not support native sharing.");
    }
  };

  // Generate story card blob per listing
  const generateStory = async (id) => {
    const generator = useStoryCard(storyRefs.current[id]);
    return await generator();
  };

  // WhatsApp Status workflow
  const shareWhatsAppStatus = async (item) => {
    const blob = await generateStory(item.destination.id);
    if (!blob) {
      alert("Could not generate story card.");
      return;
    }

    // download image
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${item.destination.city}_story.png`;
    a.click();
    URL.revokeObjectURL(url);

    // copy caption
    const caption = `Women Safety Index for ${item.destination.city} — SafeShe Atlas\n${window.location.href}`;
    await navigator.clipboard.writeText(caption);

    // open WhatsApp
    window.location.href = "whatsapp://";

    alert("Story card downloaded & caption copied.\nOpen WhatsApp → My Status → select the image.");
  };

  // Instagram Story workflow
  const shareInstagramStory = async (item) => {
    const blob = await generateStory(item.destination.id);
    if (!blob) {
      alert("Could not generate story card.");
      return;
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${item.destination.city}_instagram_story.png`;
    a.click();
    URL.revokeObjectURL(url);

    window.location.href = "instagram://";

    alert("Story card downloaded.\nOpen Instagram → Create Story → select the image.");
  };

  const getScoreClass = (score) => {
    if (score >= 80) return "bg-green-500";
    if (score >= 60) return "bg-teal-500";
    if (score >= 40) return "bg-orange-500";
    return "bg-red-500";
  };

  const getRiskLevel = (score) => {
    if (score >= 80) return "Low Risk";
    if (score >= 60) return "Moderate";
    if (score >= 40) return "Elevated";
    return "High Risk";
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <div className="pt-24 pb-16" data-testid="rankings-page">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12 fade-in">
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">Global Safety Rankings</h1>
            <p className="text-base sm:text-lg text-gray-600">
              Compare women's safety across {rankings.length} destinations
            </p>
          </div>

          {/* Search + filter */}
          <div className="bg-white rounded-2xl shadow-md p-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  placeholder="Search city or country..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 pr-4 py-3"
                />
              </div>

              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="py-3">
                  <Filter className="w-5 h-5 mr-2" />
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="score">Highest Score</SelectItem>
                  <SelectItem value="name">City Name</SelectItem>
                  <SelectItem value="trend">Best Trend</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Rankings list */}
          {loading ? (
            <div className="text-center py-20">
              <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredRankings.map((item, index) => (
                <div key={item.destination.id} className="relative">

                  {/* Hidden StoryCard for each listing */}
                  {/* Off-screen StoryCard container (must NOT use display:none) */}
                  <div
                    style={{
                      position: "absolute",
                      top: "-20000px",
                      left: "-20000px",
                      width: "1080px",
                      height: "1920px",
                      overflow: "hidden",
                      pointerEvents: "none"
                    }}
                  >
                    <div ref={(el) => (storyRefs.current[item.destination.id] = { current: el })}>
                      <StoryCard
                        city={item.destination.city}
                        country={item.destination.country}
                        score={item.metrics.composite_wsi.toFixed(0)}
                        imageUrl={item.destination.image_url}
                      />
                    </div>
                  </div>


                  {/* SHARE BUTTONS */}
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 flex flex-col gap-2 z-20">

                  {/* WhatsApp Text */}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      shareWhatsAppText(item.destination.city, pageUrl);
                    }}
                    className="p-2 bg-green-500 hover:bg-green-600 text-white rounded-full shadow transition-transform hover:scale-110"
                    title="Share link on WhatsApp"
                  >
                    {/* FIXED WhatsApp ICON */}
                    <svg className="w-4 h-4" viewBox="0 0 32 32" fill="white">
                      <path d="M16 .4A15.6 15.6 0 0 0 2.2 24.9L.4 31.6l7.8-1.8A15.6 15.6 0 1 0 16 .4zm0 28.5a12.9 12.9 0 1 1 12.9-12.9A12.9 12.9 0 0 1 16 28.9zm7.5-9.7c-.4-.2-2.3-1.1-2.6-1.2s-.6-.2-.9.2-.9 1.2-1.1 1.4-.4.3-.8.1a10.7 10.7 0 0 1-5.1-4.4c-.3-.5-.3-.9 0-1.1s.5-.6.7-.9.3-.5.5-.9 0-.7 0-1-.9-2.3-1.3-3.1-.7-.7-.9-.7h-.8a1.6 1.6 0 0 0-1.2.6A5 5 0 0 0 8 13.2a9.6 9.6 0 0 0 4.5 7.9 20.5 20.5 0 0 0 3.8 1.7c1.5.5 2.8.4 3.8.2s2.3-1 2.6-1.9.3-1.7.2-1.9-.4-.2-.8-.4z"/>
                    </svg>
                  </button>

                  {/* WhatsApp Status (story card) */}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      shareWhatsAppStatus(item);
                    }}
                    className="p-2 bg-green-600 hover:bg-green-700 text-white rounded-full shadow transition-transform hover:scale-110"
                    title="Share story card to WhatsApp Status"
                  >
                    {/* FIXED STORY ICON */}
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="white">
                      <path d="M7 2C4.2 2 2 4.2 2 7v10c0 2.8 2.2 5 5 5h10c2.8 0 5-2.2 5-5V7c0-2.8-2.2-5-5-5H7zm10 2a3 3 0 110 6 3 3 0 010-6zm-5 3a5 5 0 110 10 5 5 0 010-10z"/>
                    </svg>
                  </button>

                  {/* Instagram Story */}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      shareInstagramStory(item);
                    }}
                    className="p-2 bg-purple-600 hover:bg-purple-700 text-white rounded-full shadow transition-transform hover:scale-110"
                    title="Share story card to Instagram Story"
                  >
                    {/* FIXED IG-LIKE STORY ICON */}
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="white">
                      <path d="M7 2C4.2 2 2 4.2 2 7v10c0 2.8 2.2 5 5 5h10c2.8 0 5-2.2 5-5V7c0-2.8-2.2-5-5-5H7zm10 2a3 3 0 110 6 3 3 0 010-6zm-5 3a5 5 0 110 10 5 5 0 010-10z"/>
                    </svg>
                  </button>

                  </div>


                  {/* MAIN LIST ITEM */}
                  <Link to={`/destination/${item.destination.id}`} className="block hover-lift">
                    <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-xl transition">
                      <div className="flex items-center gap-6 pr-20">
                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-400 to-teal-400 flex items-center justify-center text-white font-bold text-xl">
                          #{index + 1}
                        </div>

                        <div className="hidden sm:block w-24 h-24 rounded-xl overflow-hidden">
                          <img
                            src={item.destination.image_url}
                            alt={item.destination.city}
                            className="w-full h-full object-cover"
                          />
                        </div>

                        <div className="flex-1">
                          <h3 className="text-xl font-bold">{item.destination.city}</h3>
                          <p className="text-sm text-gray-600 flex gap-1 items-center">
                            <MapPin className="w-4 h-4" />
                            {item.destination.country}
                          </p>

                          <div className="flex gap-3 flex-wrap mt-2">
                            <span className="text-xs bg-gray-100 px-3 py-1 rounded-full">
                              {getRiskLevel(item.metrics.composite_wsi)}
                            </span>

                            {item.metrics.trend_7day !== 0 && (
                              <span
                                className={`text-xs px-3 py-1 rounded-full flex items-center gap-1 ${
                                  item.metrics.trend_7day > 0
                                    ? "bg-green-100 text-green-700"
                                    : "bg-red-100 text-red-700"
                                }`}
                              >
                                <TrendingUp
                                  className={`w-3 h-3 ${
                                    item.metrics.trend_7day < 0 ? "rotate-180" : ""
                                  }`}
                                />
                                {item.metrics.trend_7day > 0 ? "+" : ""}
                                {item.metrics.trend_7day.toFixed(1)}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="w-20 h-20 rounded-2xl flex flex-col items-center justify-center text-white shadow-lg bg-purple-500">
                          <span className="text-2xl font-bold">
                            {item.metrics.composite_wsi.toFixed(0)}
                          </span>
                          <span className="text-xs opacity-75">WSI</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              ))}

              {filteredRankings.length === 0 && (
                <div className="text-center py-20 text-gray-500 text-lg">
                  No destinations found.
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default RankingsPage;
